import nluApi from '../../api/nlu'
import isString from 'lodash/isString'

const cacheTime = 1000 * 30 // 30 seconds

export default {
  namespaced: true,

  state: {
    features: [],
    searchText: '',
    status: {
      fetching: false,
      creating: false,
      updating: false,
      deleting: false
    },
    lastFetchTime: null
  },

  mutations: {
    UPDATE_STATUS (state, updatedStatus) {
      state.status = {
        ...state.status,
        ...updatedStatus
      }
    },

    CREATE_FEATURE (state, newFeature) {
      // unshifting to respect filter (see filter after fetching all the features)
      state.features = [
        {
          ...newFeature,
          words: isString(newFeature.words) ? newFeature.words.split(',') : newFeature.words
        },
        ...state.features
      ]
    },

    UPDATE_FEATURE (state, updatedFeature) {
      const featureIdx = state.features.findIndex(f => f.id === updatedFeature.id)
      if (featureIdx === -1) {
        console.error(`Wasn't able to update feature: ${updatedFeature.id} - does not exists`)
        return
      }
      state.features = [
        ...state.features.slice(0, featureIdx),
        {
          ...state.features[featureIdx],
          ...updatedFeature,
          words: isString(updatedFeature.words) ? updatedFeature.words.split(',') : updatedFeature.words
        },
        ...state.features.slice(featureIdx + 1)
      ]
    },

    DELETE_FEATURE (state, { id }) {
      state.features = state.features.filter(f => f.id !== id)
    },

    SET_FEATURES (state, features) {
      const _features = features.map(feature => ({
        ...feature,
        words: isString(feature.words) ? feature.words.split(',') : feature.words
      }))
      // ASC
      _features.sort((a, b) => a.name < b.name ? -1 : 1)
      state.features = _features
      state.lastFetchTime = new Date().getTime()
    }
  },

  actions: {
    async fetchFeatures ({ state, commit, dispatch, rootState, rootGetters }) {
      if (state.status.fetching) { return }
      if (state.lastFetchTime && (new Date().getTime() - state.lastFetchTime) < cacheTime) {
        console.log('Not fetching features lastFetchTime < cacheTime')
        return
      }
      commit('UPDATE_STATUS', { fetching: true })
      try {
        const features = await nluApi.listAllFeatures({ kbId: rootState.knowledgebase.current })

        if (!Array.isArray(features)) {
          throw new Error(`Array was expected got: ${typeof features}`)
        }
        commit('SET_FEATURES', features)
      } catch (e) {
        console.error("Wasn't able to fetch features:", e)
        commit('SET_FEATURES', [])
      }
      commit('UPDATE_STATUS', { fetching: false })
    },

    async createFeature ({ state, commit, dispatch, rootState, rootGetters }, feature) {
      commit('UPDATE_STATUS', { creating: true })
      try {
        const createdFeature = await nluApi.createFeature({
          kbId: rootState.knowledgebase.current,
          ...feature
        })
        commit('CREATE_FEATURE', createdFeature)
        dispatch('knowledgebase/_updateModelModifiedDate', null, { root: true })
      } catch (e) {
        console.error("Wasn't able to create feature:", e)
      }
      commit('UPDATE_STATUS', { creating: false })
    },

    async updateFeature ({ state, commit, dispatch, rootState, rootGetters }, feature) {
      commit('UPDATE_STATUS', { updating: true })
      try {
        const updatedFeature = await nluApi.updateFeature({
          kbId: rootState.knowledgebase.current,
          ...feature
        })
        commit('UPDATE_FEATURE', updatedFeature)
        dispatch('knowledgebase/_updateModelModifiedDate', null, { root: true })
      } catch (e) {
        console.error("Wasn't able to update feature:", e)
      }
      commit('UPDATE_STATUS', { updating: false })
    },

    async deleteFeature ({ rootState, commit, dispatch, rootGetters }, feature) {
      commit('UPDATE_STATUS', { deleting: true })
      try {
        await nluApi.deleteFeature({
          kbId: rootState.knowledgebase.current,
          ...feature
        })
        commit('DELETE_FEATURE', feature)

        // Feature can be used in intent & entities
        await Promise.all([
          dispatch('knowledgebase/entities/fetchAllEntities', null, { root: true }),
          dispatch('knowledgebase/fetchCategories', null, { root: true })
        ])
      } catch (e) {
        console.error("Wasn't able to delete feature:", e)
      }
      commit('UPDATE_STATUS', { deleting: false })
    }
  }
}
